import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import React, { useCallback, useEffect, useState } from 'react';

import YatungButton from '@Src/_basic/components/YatungButton';
import YatungImg from '@Src/_basic/components/YatungImg';
import YatungTable from '@Src/_basic/components/YatungTable';

import AddCircleFill from '@Src/_basic/icons/AddCircleFill';
import EditIcon from '@Src/_basic/icons/Edit';

import { customSort } from '@Src/_basic/helpers/SortHelper';
import { PostMaterialNumberRequest, PutMaterialNumberRequest } from '@Src/_basic/object/MaterialNumberManageType';
import { SpecificationData } from '@Src/_basic/object/SpecificationType';
import { TypeData } from '@Src/_basic/object/TypeType';
import { SpecificationApi } from '@Src/_basic/protocol/specification/SpecificationApi';
import { useTranslation } from 'react-i18next';
import AddModal from './AddModal';
import EditModal, { SelectedItemType } from './EditModal';

const specIcon = '/media/images/icon_spec.png';

interface Props {
  filterType: number;
  types: TypeData[];
}

const ModalIcon = () => (
  <YatungImg
    img={specIcon}
    sx={{
      width: 48,
      height: 48,
      mr: 1,
    }}
  />
);

export default function SpecsPanel({ filterType, types }: Props) {
  const { t: i18T } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [specs, setSpecs] = useState<Array<SpecificationData>>([]);
  const [filteredSpecs, setFilteredSpecs] = useState<Array<SelectedItemType>>([]);

  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedSpec, setSelectedSpec] = useState<SelectedItemType>();

  const fetchAllSpecs = () => {
    SpecificationApi.getSpecifications((allSpecs: Array<SpecificationData>) => {
      setSpecs(allSpecs.sort((a, b) => customSort(a.code, b.code)));
      setLoading(false);
    });
  };

  const filterSuppliers = (_specs: Array<SelectedItemType>) => {
    _specs &&
      setFilteredSpecs(_specs.filter((spec: SelectedItemType) => filterType === -1 || spec.typeId === filterType));
  };

  const handleSpecSelect = (data: SelectedItemType) => {
    setSelectedSpec(data);
    setIsEditModalOpen(true);
  };

  const editSpecificationHandler = useCallback((data: PutMaterialNumberRequest) => {
    setLoading(true);
    SpecificationApi.putSpecification(data, () => {
      fetchAllSpecs();
      closeModal();
    });
  }, []);

  const addSpecificationHandler = useCallback((data: PostMaterialNumberRequest) => {
    setLoading(true);
    SpecificationApi.postSpecification(data, () => {
      fetchAllSpecs();
      closeModal();
    });
  }, []);

  const closeModal = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setLoading(false);
  };

  const getTypeName = (id: number) => {
    const type = types.find((t) => t.id === id);
    return type?.name;
  };

  useEffect(() => {
    filterSuppliers(specs);
  }, [filterType, specs]);

  useEffect(() => {
    setLoading(true);
    fetchAllSpecs();
  }, []);

  return (
    <Box sx={{ border: '1px solid #6C708C', p: 1, pt: 2 }}>
      <Stack justifyContent={'flex-end'} flexDirection="row">
        <YatungButton
          text={i18T('GLOBAL.CREATE')}
          startIcon={<AddCircleFill />}
          onClick={() => setIsAddModalOpen(true)}
          color="green"
        />
      </Stack>
      <YatungTable
        loading={loading}
        data={filteredSpecs}
        sx={{
          maxHeight: 'calc(100vh - 340px)',
        }}
        columns={[
          {
            text: i18T('APPLICATIONSMANAGEMENT.TABLE.TYPE'),
            dataField: 'typeId',
            formatter: ({ cellContent }) => <>{getTypeName(cellContent as number)}</>,
            width: '120px',
          },
          {
            text: i18T('APPLICATIONSMANAGEMENT.TABLE.CODE'),
            dataField: 'code',
            width: '120px',
          },
          {
            text: i18T('APPLICATIONSMANAGEMENT.TABLE.NAME'),
            dataField: 'name',
          },
          {
            text: i18T('GLOBAL.ACTION'),
            dataField: 'action',
            width: '180px',
            formatter: ({ row }) => {
              return (
                <YatungButton
                  text={i18T('GLOBAL.UPDATE')}
                  startIcon={<EditIcon />}
                  onClick={() => {
                    handleSpecSelect(row);
                  }}
                  color="yellow"
                />
              );
            },
          },
        ]}
      />
      <AddModal
        open={isAddModalOpen}
        handleSubmit={addSpecificationHandler}
        handleClose={closeModal}
        allTypes={types}
        allItems={specs ? specs.map(({ code, name, typeId }: any) => ({ code, name, typeId })) : []}
        icon={<ModalIcon />}
        title={i18T('APPLICATIONSMANAGEMENT.TABLE.SPECIFICATION')}
      />
      {selectedSpec && (
        <EditModal
          open={isEditModalOpen}
          handleSubmit={editSpecificationHandler}
          handleClose={closeModal}
          allTypes={types}
          allItems={specs.map(({ code, name, typeId }: SpecificationData) => ({ code, name, typeId }))}
          icon={<ModalIcon />}
          title={i18T('APPLICATIONSMANAGEMENT.TABLE.SPECIFICATION')}
          selectedItem={selectedSpec}
        />
      )}
    </Box>
  );
}
